export const SAFE_MESSAGES = {
  invalidUrl: 'Enter a valid website URL, including http:// or https://.',
  unsupportedScheme: 'Only http and https URLs can be assessed.',
  credentials: 'URLs with embedded usernames or passwords are not accepted.',
  badPort: 'Only ports 80 and 443 are allowed.',
  destinationRejected:
    'This scanner only assesses public websites. Local, private, and metadata destinations are blocked.',
  dnsPrivate:
    'This website could not be scanned because its address failed public-destination checks. No request was sent.',
  dnsFailed: 'The website address could not be resolved.',
  redirectBlocked:
    'A redirect was blocked because its destination is not an allowed public website. The scan stopped before following it.',
  tooManyRedirects: 'The website redirected too many times, so the scan stopped.',
  timeout: 'The website did not respond before the scan timed out.',
  unreachable: 'The website could not be reached.',
  tls: 'The secure connection to the website could not be verified.',
  blocked:
    'The website refused or blocked the assessment request, so the response is not sufficient to judge PHP2Shell exposure.',
  generic: 'The website could not be assessed.',
  badRedirect: 'The website returned a redirect without a usable location, so the scan stopped.',
  unauthorized: 'Confirm that you own the website or have permission to assess it.',
  invalidRequest: 'Enter a valid website URL and confirm you are authorized to assess it.',
  rateLimited: 'Too many scan requests. Please wait and try again.',
  busy: 'The scanner is busy. Please try again in a moment.',
  internal: 'The scan could not be completed.',
  payloadTooLarge: 'The request is too large.',
  invalidJson: 'The request body must be valid JSON.',
  notFound: 'That API endpoint does not exist.',
} as const;

const TIMEOUT_CODES = new Set([
  'ABORT_ERR',
  'UND_ERR_CONNECT_TIMEOUT',
  'UND_ERR_HEADERS_TIMEOUT',
  'UND_ERR_BODY_TIMEOUT',
  'UND_ERR_ABORTED',
  'ETIMEDOUT',
  'TIMEOUT',
]);

const DNS_CODES = new Set(['ENOTFOUND', 'EAI_AGAIN', 'EAI_NODATA', 'UND_ERR_DNS']);

const TLS_CODES = new Set([
  'CERT_HAS_EXPIRED',
  'UNABLE_TO_VERIFY_LEAF_SIGNATURE',
  'DEPTH_ZERO_SELF_SIGNED_CERT',
  'ERR_TLS_CERT_ALTNAME_INVALID',
  'SELF_SIGNED_CERT_IN_CHAIN',
  'UNABLE_TO_GET_ISSUER_CERT',
  'UNABLE_TO_GET_ISSUER_CERT_LOCALLY',
  'CERT_SIGNATURE_FAILURE',
]);

const REACH_CODES = new Set([
  'ECONNREFUSED',
  'ECONNRESET',
  'EHOSTUNREACH',
  'ENETUNREACH',
  'EPIPE',
  'UND_ERR_CONNECT',
  'UND_ERR_SOCKET',
  'UND_ERR_CLOSED',
]);

function collectSignals(error: unknown, depth = 0, codes: string[] = [], names: string[] = []): void {
  if (!error || depth > 5) return;
  if (typeof error !== 'object') return;
  const record = error as { code?: unknown; cause?: unknown; errors?: unknown; name?: unknown };
  if (typeof record.code === 'string') codes.push(record.code);
  if (typeof record.name === 'string') names.push(record.name);
  if (Array.isArray(record.errors)) {
    for (const nested of record.errors) collectSignals(nested, depth + 1, codes, names);
  }
  if (record.cause) collectSignals(record.cause, depth + 1, codes, names);
}

export function mapFetchError(error: unknown): string {
  const codes: string[] = [];
  const names: string[] = [];
  collectSignals(error, 0, codes, names);

  if (names.includes('AbortError') || names.includes('TimeoutError')) {
    return SAFE_MESSAGES.timeout;
  }
  if (codes.some((code) => TIMEOUT_CODES.has(code))) return SAFE_MESSAGES.timeout;
  if (codes.some((code) => DNS_CODES.has(code))) return SAFE_MESSAGES.dnsFailed;
  if (codes.some((code) => TLS_CODES.has(code))) return SAFE_MESSAGES.tls;
  if (codes.some((code) => REACH_CODES.has(code))) return SAFE_MESSAGES.unreachable;
  return SAFE_MESSAGES.generic;
}
